import { useEffect, useState, useRef, useLayoutEffect } from "react";
import PropTypes from 'prop-types';
import {
    Box,
    Paper,
    Typography,
    IconButton,
    TextField,
    Stack,
    LinearProgress,
    Tooltip,
} from "@mui/material";

import Iconify from "src/components/iconify";
import { useListChatAi, usePostChatAiMutation } from 'src/sections/AI/ai-api-request'
import LoadingButton from "@mui/lab/LoadingButton";
import { useAuthContext } from 'src/auth/hooks';

const nombreAgente = 'Becca Brain'

export default function ChatView({ openChat = false, handleClose }) {
    const { user: dataUser } = useAuthContext();
    const [message, setMessage] = useState("");
    const [messages, setMessages] = useState([]);
    const { listChatAiQuery } = useListChatAi()
    const { postChatAiMutation } = usePostChatAiMutation()
    const [chats, setChats] = useState([])
    const bottomRef = useRef(null);
    const containerRef = useRef(null);
    const [mensajesUsados, setMensajesUsados] = useState(() => {
        const empresa = dataUser?.empresas?.find((x) => x.id === dataUser?.idEmpresa);
        return empresa?.total_mensajes || 0;
    });

    useEffect(() => {
        const empresa = dataUser?.empresas?.find((x) => x.id === dataUser?.idEmpresa);
        setMensajesUsados(empresa?.total_mensajes || 0);
    }, [dataUser]);

    const totalMensajes = dataUser?.totalBeccaChat ?? dataUser?.totalBeccaChat ?? 20;
    const limiteAlcanzado = mensajesUsados >= totalMensajes;

    const mensajeBienvenida = `Hola ${dataUser?.nombre} 👋 soy ${nombreAgente}, tu asistente empresarial. Estoy aquí para ayudarte a descubrir 
    información sobre tu empresa, entender tus datos y resolver dudas sobre tu negocio. Cuento con Información con 6 meses de antiguedad
    ¿Qué te gustaría consultar?`

    const scrollToBottom = () => {
        if (bottomRef.current) {
            bottomRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
        }
        if (containerRef.current) {
            containerRef.current.scrollTop = containerRef.current.scrollHeight;
        }
    };

    useLayoutEffect(() => {
        scrollToBottom();
    }, [chats]);

    useEffect(() => {
        if (listChatAiQuery?.data?.data?.data) {
            setChats(listChatAiQuery.data.data.data);
        }
    }, [listChatAiQuery?.data?.data?.data]);

    useEffect(() => {
        if (openChat) {
            const today = new Date().toISOString().split("T")[0];
            const lastWelcomeDate = localStorage.getItem("welcomeMessageDate");
            const bienvenidaEnviado = chats.find((x) => x.mensaje === mensajeBienvenida)
            if (!bienvenidaEnviado && lastWelcomeDate !== today) {
                const welcomeMessage = {
                    mensaje: mensajeBienvenida,
                    tipo: "IA",
                    fechaCreacion: new Date(),
                };
                setChats((prev) => [...prev, welcomeMessage]);
                localStorage.setItem("welcomeMessageDate", today);
            }
            setTimeout(() => {
                scrollToBottom();
            }, 20);
        }
    }, [openChat, mensajeBienvenida, chats]);

    const handleSend = async () => {
        try {
            if (!message.trim()) return;

            const userMessage = {
                mensaje: message,
                tipo: "U",
                fechaCreacion: new Date(),
            };

            setChats((prev) => [...prev, userMessage]);

            const datapayload = {
                mensaje: message,
                chats
            };

            setMessage("");

            const response = await postChatAiMutation.mutateAsync(datapayload);
            const botMessage = {
                mensaje: response.data,
                tipo: "IA",
                fechaCreacion: new Date(),
            };

            setChats((prev) => [...prev, botMessage]);
            setMensajesUsados((prev) => prev + 1);

        } catch (error) {
            console.error(error);
        }
    };

    return (
        <>
            {/* PANEL */}
            {openChat && (
                <Paper
                    elevation={10}
                    sx={{
                        position: "fixed",
                        bottom: 265,
                        right: 24,
                        width: 380,
                        height: 500,
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: 3,
                        overflow: "hidden",
                        zIndex: 9999,
                        boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
                    }}
                >

                    {/* HEADER */}
                    <Box
                        sx={{
                            p: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            bgcolor: "primary.main",
                            color: "white",
                        }}
                    >
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Iconify icon="mdi:brain" width={28} sx={{ color: "white", flexShrink: 0 }} />
                            <Box>
                                <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
                                    {nombreAgente}
                                </Typography>
                                <Typography variant="caption" sx={{ opacity: 0.85, fontSize: 11 }}>
                                    Información de los últimos 6 meses
                                </Typography>
                            </Box>
                        </Stack>
                        <IconButton onClick={handleClose} sx={{ color: "white" }}>
                            <Iconify icon="mingcute:close-line" />
                        </IconButton>
                    </Box>

                    {/* BARRA DE USO DEL PLAN */}
                    {(() => {
                        // const usado = chats.filter((c) => c.tipo === 'U').length;
                        const total = totalMensajes;
                        const pct = Math.min((mensajesUsados / total) * 100, 100);
                        const getPlanInfo = () => {
                            if (pct >= 85) return { barColor: 'error', labelColor: '#d32f2f' };
                            if (pct >= 60) return { barColor: 'warning', labelColor: '#e65100' };
                            return { barColor: 'success', labelColor: '#2e7d32' };
                        };
                        const { barColor, labelColor } = getPlanInfo();
                        return (
                            <Box sx={{ px: 2, pt: 1.2, pb: 1, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                        Mensajes del plan
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: labelColor }}>
                                        {mensajesUsados} / {total}
                                    </Typography>
                                </Stack>
                                <LinearProgress
                                    variant="determinate"
                                    value={pct}
                                    color={barColor}
                                    sx={{ height: 5, borderRadius: 4 }}
                                />
                            </Box>
                        );
                    })()}

                    {/* MENSAJES */}
                    <Stack
                        ref={containerRef}
                        spacing={2}
                        sx={{
                            flex: 1,
                            p: 2,
                            overflowY: "auto",
                            bgcolor: "#f6f7fb",
                        }}
                    >
                        {chats.map((msg, index) => {

                            const isUser = msg.tipo === "U";

                            return (
                                <Box
                                    key={index}
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: isUser ? "flex-end" : "flex-start",
                                    }}
                                >

                                    <Box
                                        sx={{
                                            px: 2,
                                            py: 1.2,
                                            borderRadius: 2,
                                            maxWidth: "75%",
                                            fontSize: 14,
                                            lineHeight: 1.4,
                                            bgcolor: isUser ? "primary.main" : "white",
                                            color: isUser ? "white" : "text.primary",
                                            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                        }}
                                        dangerouslySetInnerHTML={{ __html: msg.mensaje }}
                                    />
                                    {/* FECHA */}
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            mt: 0.5,
                                            color: "text.secondary",
                                            fontSize: 11,
                                        }}
                                    >
                                        {new Date(msg.fechaCreacion).toLocaleString()}
                                    </Typography>

                                </Box>
                            );
                        })}
                        {postChatAiMutation.isPending && (
                            <Typography
                                variant="body2"
                                sx={{
                                    fontStyle: "italic",
                                    color: "text.secondary",
                                }}
                            >
                                {nombreAgente} está escribiendo...
                            </Typography>
                        )}
                        <div ref={bottomRef} style={{ height: 1 }} />
                    </Stack>

                    {/* INPUT */}
                    <Box
                        sx={{
                            p: 2,
                            borderTop: "1px solid #eee",
                            bgcolor: "white",
                        }}
                    >
                        <Stack direction="row" spacing={1}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder={limiteAlcanzado ? "Límite de mensajes alcanzado" : "Escribe un mensaje..."}
                                value={message}
                                disabled={limiteAlcanzado}
                                onChange={(e) => setMessage(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !limiteAlcanzado) handleSend();
                                }}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: 3,
                                    },
                                }}
                            />

                            <Tooltip
                                title={limiteAlcanzado ? "Has alcanzado el límite de mensajes de tu plan" : ""}
                                arrow
                            >
                                <span>
                                    <LoadingButton
                                        loading={postChatAiMutation.isPending}
                                        disabled={limiteAlcanzado}
                                        color="primary"
                                        onClick={handleSend}
                                        sx={{
                                            bgcolor: limiteAlcanzado ? "action.disabledBackground" : "primary.main",
                                            color: "white",
                                            "&:hover": {
                                                bgcolor: "primary.dark",
                                            },
                                        }}
                                    >
                                        <Iconify icon="mdi:send" />
                                    </LoadingButton>
                                </span>
                            </Tooltip>
                        </Stack>
                    </Box>
                </Paper>
            )}

        </>
    );
}
ChatView.propTypes = {
    openChat: PropTypes.bool,
    handleClose: PropTypes.func
};